/**
 * Keeps the project scope alive (cards 1.1, 2.1, 7.1). Rendered once, by the
 * title bar, on every panel — the switcher itself only shows where the scope
 * applies, but the scope must load and follow everywhere. Renders nothing.
 *
 *   1. Once the API workspaces are loaded: run the migration (idempotent,
 *      marked per bucket) and load the known projects into the scope store.
 *      Reloaded when the team roster changes (a team project appears or goes)
 *      and on `refreshProjectScope()` from anywhere (the switcher, Projects).
 *   2. Project → workspace: the active scope (a project, or No project) opens
 *      its own workspace — the last used there, else its first — or closes
 *      the open one when it has none (projectWorkspaceFollow.followScope).
 *      All projects keeps whatever is open.
 *   3. Workspace → project: a workspace picked by hand moves the scope to the
 *      project bound to it. Skipped for the switch this component made itself
 *      and for the very first workspace (the start-up restore: the persisted
 *      project wins, step 2).
 */
import { useEffect, useRef } from 'react';
import { useTeamsStore } from '../store/teamsStore';
import { useGraphUiStore } from '../graph/graphUiStore';
import { runProjectScopeMigration } from './projectScopeMigration';
import { enqueueScopeJob } from './projectScopeJobs';
import { refreshProjectScope } from './projectScopeLoader';
import { setActiveProject, useProjectScope, useProjectScopeStore } from './projectScopeStore';
import { followScope, projectForWorkspace } from './projectWorkspaceFollow';
import { ALL_PROJECTS, NO_PROJECT, normalizeScopeId } from './projectScopeFilter';
import { holdsWorkspace, workspaceIdsOf } from './projectBindingFields';
import { lastWorkspaceFor, rememberWorkspace } from './projectLastWorkspace';
import { bindWorkspace } from './projectBinding';

/**
 * Collection ids in one workspace, wherever it is stored (cloud: the API; local
 * / git: its snapshot). The readers load lazily: the title bar renders this
 * component, and the snapshot stack must not load with it (nor in its tests).
 */
export async function collectionIdsOf(workspace, isAuthenticated) {
  const storage = workspace?.storage;
  const onDevice = storage === 'local' || storage === 'git';
  if (!onDevice && !isAuthenticated) throw new Error('cloud workspace while signed out');
  const cols = onDevice
    ? (await (await import('../services/workspaceStoreRouter')).loadSnapshotById([workspace], workspace.id))?.collections
    : await (await import('../services/collectionService')).collectionService.getCollections(workspace.id);
  return (cols || []).map((c) => c?.id).filter((id) => id != null);
}

/** The per-device "last used workspace" key of a scope: the project id, 'none' or 'all'. */
const memoryKeyOf = (scope) => scope?.activeProject?.id ?? normalizeScopeId(scope?.activeProjectId);

const signatureOf = (workspaces) => (workspaces || []).map((w) => `${w?.id}:${w?.storage || ''}`).join('|');

export default function ProjectScopeSync({ workspaces = [], currentWorkspace = null, onWorkspaceChange, isAuthenticated = false }) {
  const scope = useProjectScope();
  const teams = useTeamsStore((s) => s.teams);
  const sig = signatureOf(workspaces);
  const autoSwitchRef = useRef(null);
  const lastWorkspaceRef = useRef(undefined);
  const knownIdsRef = useRef(null); // workspace ids seen so far; null before the first list
  const clearedRef = useRef(false); // step 2 closed the open workspace (the scope has none)

  // 1. Migrate (when there is something to migrate) and load.
  useEffect(() => {
    const list = workspaces;
    enqueueScopeJob(async () => {
      try {
        if (list.length) {
          await runProjectScopeMigration({
            workspaces: list,
            loadCollectionIds: (ws) => collectionIdsOf(ws, isAuthenticated),
            // No current workspace: the first run does NOT activate a project.
            // The new projects hold no boards or documents yet, so activating
            // one would empty Boards and Documents right after the update
            // (found by the Boards e2e, 20.10.2026). The scope starts at All.
            currentWorkspaceId: null,
          });
        }
      } catch (err) {
        console.warn('[project-scope] migration failed:', err);
      }
      await refreshProjectScope().catch((err) => console.warn('[project-scope] load failed:', err));
    });
    // The workspace LIST (ids + storage) and the roster drive this, not every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, teams, isAuthenticated]);

  // Projects created / renamed / deleted in the Projects view (it bumps the
  // graph after every change): reload, no migration.
  const graphVersion = useGraphUiStore((s) => s.graphVersion);
  const firstVersion = useRef(true);
  useEffect(() => {
    if (firstVersion.current) {
      firstVersion.current = false;
      return;
    }
    enqueueScopeJob(() => refreshProjectScope().catch((err) => console.warn('[project-scope] reload failed:', err)));
  }, [graphVersion]);

  // 2. Project → workspace. Runs when the PROJECT changes (or the workspace
  // list loads), never when the open workspace changes: a workspace picked by
  // hand is step 3's to answer, and reacting to it here switched straight back
  // (an endless loop between the two, React error 185).
  const currentId = currentWorkspace?.id ?? null;
  const currentIdRef = useRef(currentId);
  currentIdRef.current = currentId;
  const known = useProjectScopeStore((s) => s.known);
  // "No project" depends on what EVERY project holds, not only the active one.
  const heldSig = known.loaded ? known.projects.map((p) => `${p?.id}:${workspaceIdsOf(p).join(',')}`).join('|') : '';
  useEffect(() => {
    // A workspace created a moment ago is not in any project yet: step 4
    // (below, same commit) adds it to the active one, and the project's new
    // workspace list re-runs this. Answering now would close it again.
    const before = knownIdsRef.current;
    const opened = currentIdRef.current;
    if (before && opened != null && !before.has(String(opened)) && workspaces.some((w) => String(w?.id) === String(opened))) return;
    const action = followScope({
      scope,
      projects: known.projects,
      workspaces,
      currentWorkspaceId: currentIdRef.current,
      lastUsedId: lastWorkspaceFor(memoryKeyOf(scope)),
      projectsLoaded: known.loaded,
    });
    if (!action || !onWorkspaceChange) return;
    const ws = action.workspace;
    // A switch this component makes is not a hand pick (step 3 skips it).
    autoSwitchRef.current = ws ? String(ws.id) : null;
    clearedRef.current = !ws;
    onWorkspaceChange(ws);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.activeProjectId, scope.status, workspaceIdsOf(scope.activeProject).join('|'), sig, known.loaded, heldSig]);

  // 3. Workspace → project.
  useEffect(() => {
    const previous = lastWorkspaceRef.current;
    lastWorkspaceRef.current = currentId;
    if (currentId == null) return;
    const state = useProjectScopeStore.getState();
    const projects = state.known.projects;
    if (autoSwitchRef.current === String(currentId)) {
      autoSwitchRef.current = null;
      clearedRef.current = false;
      return;
    }
    // Start-up restore: the first workspace that loads (from none) is not a
    // pick — unless step 2 closed the previous one (then this is a choice
    // made from the empty state, e.g. a new workspace).
    if (previous == null && !clearedRef.current) return;
    clearedRef.current = false;
    const project = projectForWorkspace({ scope: state, projects, workspaces, workspaceId: currentId });
    if (project) setActiveProject(project.id);
    // Remember it as the scope's last-used workspace (21.10.2026; No project
    // and All projects since 26.10.2026).
    const holder = project || state.activeProject;
    if (holdsWorkspace(holder, currentId)) rememberWorkspace(holder.id, currentId);
    else if (!project) {
      const id = normalizeScopeId(state.activeProjectId);
      const unassigned = !projects.some((p) => holdsWorkspace(p, currentId));
      if (id === ALL_PROJECTS || (id === NO_PROJECT && unassigned)) rememberWorkspace(id, currentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // 4. A workspace created while a project is active joins it (owner,
  // 21.10.2026: a project holds several API workspaces). "Created" = an id
  // that was not in the previous list and is the one now open; the first
  // list a session loads is never a creation.
  useEffect(() => {
    const ids = (workspaces || []).map((w) => String(w?.id));
    const before = knownIdsRef.current;
    knownIdsRef.current = new Set(ids);
    if (!before || currentId == null || before.has(String(currentId)) || !ids.includes(String(currentId))) return;
    const state = useProjectScopeStore.getState();
    const project = state.status === 'ready' ? state.activeProject : null;
    if (!project || state.known.projects.some((p) => holdsWorkspace(p, currentId))) return;
    const ws = workspaces.find((w) => String(w?.id) === String(currentId));
    enqueueScopeJob(async () => {
      try {
        await bindWorkspace(project, ws);
        await refreshProjectScope();
      } catch (err) {
        console.warn('[project-scope] could not add the new workspace to the project:', err);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, currentId]);

  return null;
}
